import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Analytics } from '@vercel/analytics/react';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { supabase } from '@/utils/supabaseClient';
import { setUser, setToken } from '@/store/slices/authSlice';

// Lazy load page components for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ListingDetailsPage = lazy(() => import('./pages/ListingDetailsPage'));
const PropertyManagementPage = lazy(() => import('./pages/PropertyManagementPage'));
const ListPropertyPage = lazy(() => import('./pages/ListPropertyPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const SafetyPage = lazy(() => import('./pages/SafetyPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const KYCPage = lazy(() => import('./pages/KYCPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const SavedPropertiesPage = lazy(() => import('./pages/SavedPropertiesPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const ScheduleViewingPage = lazy(() => import('./pages/ScheduleViewingPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Lazy load dashboard components
const TenantDashboard = lazy(() => import('./components/Dashboard/TenantDashboard'));
const LandlordDashboard = lazy(() => import('./components/Dashboard/LandlordDashboard'));
const AdminDashboard = lazy(() => import('./components/Dashboard/AdminDashboard'));

// Protected route wrapper - keep synchronous for auth checks
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Page loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner />
      <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  const dispatch = useDispatch();
  const [appReady, setAppReady] = useState(false);

  // Initialize auth state from Supabase session on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Failed to get session:', error);
          setAppReady(true);
          return;
        }

        if (session?.user) {
          console.log('Auth initialized with user:', session.user.email);
          // Sync to Redux store
          dispatch(setToken(session.access_token));
          dispatch(setUser({
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.name || '',
            role: session.user.user_metadata?.role || 'tenant',
            created_at: new Date(),
            updated_at: new Date(),
          } as any));
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setAppReady(true);
      }
    };

    initializeAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        dispatch(setToken(session.access_token));
        dispatch(setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || '',
          role: session.user.user_metadata?.role || 'tenant',
          created_at: new Date(),
          updated_at: new Date(),
        } as any));
      }
    });

    return () => subscription?.unsubscribe();
  }, [dispatch]);

  // Handle auth callback with hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && (hash.includes('access_token') || hash.includes('type=signup'))) {
      // Don't redirect recovery tokens - let them go to reset-password page
      if (hash.includes('type=recovery')) {
        window.location.href = '/reset-password' + hash;
        return;
      }
      // Redirect other auth callbacks (signup, magiclink, etc.)
      window.location.href = '/auth/callback' + hash;
    }
  }, []);

  // Show loading screen while initializing auth
  if (!appReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Analytics />
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/safety" element={<SafetyPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/properties" element={<PropertiesPage />} />
          <Route path="/property/:id" element={<ListingDetailsPage />} />
          <Route path="/payment/:propertyId" element={<PaymentPage />} />
          <Route path="/list-property" element={<ListPropertyPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/property-management"
            element={
              <ProtectedRoute>
                <PropertyManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenant-dashboard"
            element={
              <ProtectedRoute>
                <TenantDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/landlord-dashboard"
            element={
              <ProtectedRoute>
                <LandlordDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kyc"
            element={
              <ProtectedRoute>
                <KYCPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-properties"
            element={
              <ProtectedRoute>
                <SavedPropertiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute>
                <ApplicationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <SchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/landlord/schedule-viewing"
            element={
              <ProtectedRoute>
                <ScheduleViewingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          {/* Catch-all route for 404 pages */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
}

export default App;
